import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import apiClient from '@/api/client';
import { formatPlateNumber } from '@/lib/utils';
import { displayName } from '@/constants/customerTitles';

interface WarrantyInfo {
  jobNumber: string;
  customerName: string | null;
  plateNumber: string | null;
  lacquerType: string | null;
  warrantyMonths: number;
  warrantyPeriodLabel: string;
  issuedAt: string;
  expiryDate: string;
  valid: boolean;
  companyName: string;
}

export default function WarrantyVerifyPage() {
  const { identifier } = useParams<{ identifier: string }>();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['warranty-verify', identifier],
    queryFn: () =>
      apiClient.get<WarrantyInfo>(`/warranty/verify/${identifier}`).then((r) => r.data),
    retry: false,
  });

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('ms-MY', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-6 w-6 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="bg-card rounded-xl border border-red-200 p-8 max-w-sm w-full text-center shadow">
          <div className="text-4xl mb-3">❌</div>
          <h1 className="text-lg font-semibold text-foreground mb-1">Jaminan Tidak Sah</h1>
          <p className="text-sm text-muted-foreground">Warranty Not Found</p>
          <p className="text-xs text-muted-foreground mt-3">Ref: {identifier}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <div className="bg-card rounded-xl border border-border shadow-lg p-8 max-w-md w-full">
        {/* Status badge */}
        <div
          className={`text-center mb-6 py-3 rounded-lg ${data.valid ? 'bg-green-50 dark:bg-green-950 border border-green-200' : 'bg-red-50 dark:bg-red-950 border border-red-200'}`}
        >
          <div className="text-3xl mb-1">{data.valid ? '✅' : '⚠️'}</div>
          <p className={`font-bold text-lg ${data.valid ? 'text-green-700' : 'text-red-600'}`}>
            {data.valid ? 'Jaminan Sah / Valid Warranty' : 'Jaminan Tamat / Warranty Expired'}
          </p>
        </div>

        {/* Workshop */}
        <p className="text-center text-xs text-muted-foreground uppercase tracking-wide mb-4">
          {data.companyName}
        </p>

        {/* Details */}
        <div className="space-y-3 text-sm">
          <Row label="No. Kerja / Job No." value={data.jobNumber} mono />
          <Row label="Nama Pelanggan / Customer" value={displayName(data.customerName)} />
          <Row
            label="No. Plat / Plate No."
            value={formatPlateNumber(data.plateNumber) ?? '—'}
            mono
          />
          <Row label="Jenis Lacquer / Lacquer Type" value={data.lacquerType ?? '—'} />
          <Row label="Tempoh Jaminan / Warranty Period" value={data.warrantyPeriodLabel} />
          <Row label="Tarikh Dikeluarkan / Issued" value={formatDate(data.issuedAt)} />
          <Row
            label="Tarikh Tamat / Expiry"
            value={formatDate(data.expiryDate)}
            highlight={!data.valid}
          />
        </div>

        {/* Coverage */}
        <div className="mt-6 pt-4 border-t border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Skop Jaminan / Warranty Covers
          </p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>
              <Check className="w-3 h-3 inline -mt-0.5 mr-1" />
              Cat Rosak — Paint damage / peeling
            </li>
            <li>
              <Check className="w-3 h-3 inline -mt-0.5 mr-1" />
              Cat Kusam — Dull / faded paint
            </li>
            <li>
              <Check className="w-3 h-3 inline -mt-0.5 mr-1" />
              Cat Mati — Dead / flat paint
            </li>
          </ul>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Disahkan oleh sistem BengkelOS · Verified by BengkelOS
        </p>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  highlight,
}: {
  label: string;
  value: string;
  mono?: boolean;
  highlight?: boolean;
}) {
  return (
    <div className="flex justify-between items-start gap-2">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span
        className={`font-medium text-right ${mono ? 'font-mono' : ''} ${highlight ? 'text-red-600' : 'text-foreground'}`}
      >
        {value}
      </span>
    </div>
  );
}
